import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchControlApiMethodOptions } from '../api/utils/fetchControlApiMethodOptions';
import { useRequestDataContext } from './context/useRequestDataContext';
import { isApiMethodItems } from '../utils/isApiMethodItems';
import {
  ControlItemValue,
  ControlItems,
  StaticControlItem,
} from '../types/types';
import { useResponseActionsContext } from './context/useResponseActionsContext';

interface ControlApiMethodItemsResult {
  options: StaticControlItem[];
  isLoading: boolean;
}

// Maps static control items array to { value, label } options
const mapStaticItems = (items: any[]): StaticControlItem[] =>
  items.map((item) => ({
    value: item.value as ControlItemValue,
    label: String(item.label ?? item.value),
  }));

// Resolves control items to options — supports static array and dynamic apiMethod
// requestData is read from RequestDataContext; missing provider throws
// `enabled` defers fetch for popover-based controls (multiCheckbox/combobox) until user opens the popover
export const useControlApiMethodItems = (
  controlItems: ControlItems | undefined,
  enabled: boolean = true
): ControlApiMethodItemsResult => {
  const requestData = useRequestDataContext();
  const { getResponseActionHandler } = useResponseActionsContext();

  const [asyncOptions, setAsyncOptions] = useState<StaticControlItem[]>([]);
  // Start as true when fetch will be deferred (popover not yet opened) to avoid flash of "no options" on first open
  const [isLoading, setIsLoading] = useState<boolean>(
    !enabled && !!controlItems && isApiMethodItems(controlItems)
  );
  // useRef (not useState) — gates the effect across re-renders without triggering its own render.
  // Prevents refetch when parent re-renders cause controlItems reference to change (e.g., on every checkbox click).
  const hasFetchedRef = useRef<boolean>(false);

  // Static items path — synchronous mapping, no loading
  const staticOptions = useMemo<StaticControlItem[]>(() => {
    if (!controlItems || !Array.isArray(controlItems)) {
      return [];
    }
    return mapStaticItems(controlItems);
  }, [controlItems]);

  useEffect(() => {
    // Skip fetching when disabled (lazy mode), items are static array or undefined, requestData is missing, or already fetched once
    if (
      !enabled ||
      !controlItems ||
      !isApiMethodItems(controlItems) ||
      !requestData ||
      hasFetchedRef.current
    ) {
      return;
    }
    // Mark before fetch to avoid duplicate in-flight requests on rapid re-renders
    hasFetchedRef.current = true;

    const fetchItems = async () => {
      setIsLoading(true);
      try {
        const mappedOptions = await fetchControlApiMethodOptions(
          controlItems,
          requestData,
          getResponseActionHandler
        );
        setAsyncOptions(mappedOptions);
      } catch (error) {
        console.error('Failed to fetch control apiMethod items', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [controlItems, requestData, enabled]);

  // Return static options if items are array, else async-fetched (empty until resolved)
  const options =
    controlItems && Array.isArray(controlItems) ? staticOptions : asyncOptions;

  return { options, isLoading };
};
